import AccountCircle from '@mui/icons-material/AccountCircle';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Close from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import InfoIcon from '@mui/icons-material/Info';
import NoAccounts from '@mui/icons-material/NoAccounts';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Modal from '@mui/material/Modal';
import Pagination from '@mui/material/Pagination';
import Snackbar from '@mui/material/Snackbar';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  Button,
  Paper,
  TableContainer,
  useColorScheme,
} from '@mui/material';
import { updateBarnHorse } from '../../../../../redux/barn/actions';
import { getHorseAccess } from '../../../../../redux/horseAccess/actions';

import { primary } from '../../../../../utils/brandColors';

import { formatDateString } from '../../../../../utils/dateHelper';
import useDebounce from '../../../../../utils/useDebounce';
import AddEditModal from './components/AddEditModal';

const HorseAccessComponent = ({
  id,
  isAdmin,
}) => {
  const dispatch = useDispatch();
  const { mode } = useColorScheme();

  // Redux state
  const {
    horseAccess,
    isFetchingHorseAccess,
  } = useSelector((state) => state.horseAccess);
  const {
    currentHorseLodging,
    isFetchingBarnHorse,
  } = useSelector((state) => state.horse);
  const {
    selectedBarn,
    questriSubscription,
  } = useSelector((state) => state.barn);

  // State
  const [pageSize] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [sortModel] = React.useState([
    {
      field: 'endDate',
      sort: 'desc',
    },
  ]);
  const [snackbar, setSnackbar] = React.useState(null);
  const [openAddEditModal, setOpenAddEditModal] = React.useState(false);
  const [selectedHorseAccess, setSelectedHorseAccess] = React.useState(null);
  const [showInfoModal, setShowInfoModal] = React.useState(false);
  const [price, setPrice] = React.useState('35');

  const debouncedPrice = useDebounce(price, 600);

  React.useEffect(() => {
    dispatch(getHorseAccess(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
  }, [id, page]);

  React.useEffect(() => {
    if (currentHorseLodging.createBillableForPaidClientAccessSeat) {
      dispatch(updateBarnHorse(selectedBarn.id, id, { paidClientAccessSeatBillingAmount: parseFloat(debouncedPrice).toFixed(2) }))
        .then(() => {
          setSnackbar({
            children: 'Success! Client Experience billable amount updated.',
            severity: 'success',
          });
        })
        .catch((error) => {
          console.error(error);
          setSnackbar({
            children: error.message || 'There was an error updating this Client Experience billable amount.',
            severity: 'error',
          });
        });
    }
  }, [debouncedPrice])

  React.useEffect(() => {
    if (currentHorseLodging) {
      const isMonthly = questriSubscription
        && ((questriSubscription.currentPeriodEnd.getTime()) - (questriSubscription.currentPeriodStart.getTime()) < 3456000000)
      setPrice((currentHorseLodging.paidClientAccessSeatBillingAmount
        && currentHorseLodging.paidClientAccessSeatBillingAmount.toString())
        || (isMonthly
          ? '35'
          : '420'))
    }
  }, [currentHorseLodging, questriSubscription])

  const handleAddClick = () => {
    setSelectedHorseAccess(null);
    setOpenAddEditModal(true);
  };

  const handleCloseAddEditModal = () => {
    setOpenAddEditModal(false);
    // reload
    dispatch(getHorseAccess(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
  };

  return (
    <Paper
      sx={{ borderRadius: 3 }}
    >
      <CardHeader
        sx={{
          backgroundColor: currentHorseLodging.paidClientAccessSeat
            ? primary[500]
            : 'inherit',
          color: currentHorseLodging.paidClientAccessSeat
            ? 'white'
            : 'inherit',
          alignItems: 'flex-start',
          p: 2,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
        }}
        avatar={<Avatar><GroupIcon /></Avatar>}
        action={(isAdmin && (
          <div>
            <Button
              variant="contained"
              aria-label="add user access"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              sx={{
                ml: 1,
                color: (currentHorseLodging.paidClientAccessSeat || mode === 'dark')
                  ? 'black'
                  : '#fff !important',
                background: currentHorseLodging.paidClientAccessSeat
                  ? '#fff !important'
                  : primary[500],
              }}
            >
              Add User
            </Button>
          </div>
        ))}
        title="Manage Horse Users"
        subheaderTypographyProps={{
          sx: {
            textWrap: 'auto',
            color: currentHorseLodging.paidClientAccessSeat
              ? 'white'
              : 'inherit',
          },
        }}
        subheader={(
          <>
            <Typography variant="caption" component="div" sx={{ textWrap: 'auto' }}>
              Manage which users have access to this horse and the type of access they have.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <FormControlLabel
                sx={{ mb: 0 }}
                control={(
                  <Switch
                    color="secondary"
                    checked={!!currentHorseLodging.paidClientAccessSeat}
                    disabled={isFetchingBarnHorse || isFetchingHorseAccess}
                    onChange={(e) => {
                      dispatch(updateBarnHorse(selectedBarn.id, id, { paidClientAccessSeat: e.target.checked }))
                        .then(() => {
                          setSnackbar({
                            children: !e.target.checked
                              ? 'Success! Clients with access to this horse now have access to Questri mobile.'
                              : 'Success! Paid client experience has been removed. Clients will no longer have access to Questri mobile.',
                            severity: 'success',
                          });
                        })
                        .catch((error) => {
                          console.error(error);
                          setSnackbar({
                            children: error.message || 'There was an error switching this horse to a paid client experience seat.',
                            severity: 'error',
                          });
                        });
                    }}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                )}
                label={`User Access - Questri Mobile  (${currentHorseLodging.paidClientAccessSeat
                  ? 'ENABLED'
                  : 'DISABLED'})`}
              />

              <Tooltip title="About Client Experience">
                <IconButton
                  sx={{
                    color: currentHorseLodging.paidClientAccessSeat
                      ? 'white'
                      : 'primary',
                  }}
                  onClick={() => setShowInfoModal(true)}
                >
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
      />
      {
        !isAdmin
          ? <CardContent sx={{ p: 2 }}>You should not be here... Insufficient permisions</CardContent>
          : (
            <CardContent sx={{ p: 2 }}>
              <TableContainer sx={{ mb: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        align="center"
                        style={{
                          maxWidth: 90,
                          minWidth: 90,
                        }}
                      >Avatar
                      </TableCell>
                      <TableCell align="left">Name</TableCell>
                      <TableCell align="left">Account</TableCell>
                      <TableCell align="left">Access Type</TableCell>
                      <TableCell align="left">Note</TableCell>
                      <TableCell align="left">Ownership</TableCell>
                      <TableCell align="left">Access Start</TableCell>
                      <TableCell align="left">Access End</TableCell>
                      <TableCell
                        align="center"
                        style={{
                          maxWidth: 90,
                          minWidth: 90,
                        }}
                      >Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody
                    style={{
                      height: 100,
                      overflowY: 'auto',
                      overflowX: 'auto',
                    }}
                  >
                    {horseAccess.content.map((ha) => (
                      <TableRow key={ha.id}>
                        <TableCell
                          align="center"
                          style={{
                            maxWidth: 90,
                            minWidth: 90,
                          }}
                        ><IconButton aria-haspopup="false"> <Avatar src={ha.user.avatar} /> </IconButton>
                        </TableCell>
                        <TableCell align="left">{ha.user.userFullName}</TableCell>
                        <TableCell align="left">
                          {
                            ha.user.auth0UserId
                              ? (<AccountCircle color="primary" />)
                              : (<Tooltip title="No account for this user. Make sure they have an email and have logged into Questri"><NoAccounts color="secondary" /></Tooltip>)
                          }
                        </TableCell>
                        <TableCell align="left">{ha.type}</TableCell>
                        <TableCell align="left">
                          {ha.accessNote && <Typography variant="caption" component="div">{`"${ha.accessNote}"`}</Typography>}
                        </TableCell>
                        <TableCell align="left">
                          {ha.owner && `${ha.ownershipPercentage}%`}
                          {ha.owner && ha.ownershipNotes && <Typography variant="caption" component="div">{`"${ha.ownershipNotes}"`}</Typography>}
                        </TableCell>
                        <TableCell align="left">{formatDateString(ha.startDate)}</TableCell>
                        <TableCell align="left">{ha.endDate && formatDateString(ha.endDate)}</TableCell>
                        <TableCell
                          align="center"
                          style={{
                            maxWidth: 90,
                            minWidth: 90,
                          }}
                        >
                          <IconButton edge="end" aria-label="edit" onClick={() => { setSelectedHorseAccess(ha); setOpenAddEditModal(true); }}>
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {(horseAccess.totalPages || 0) > 1 && (
                  <Pagination
                    sx={{ my: 1 }}
                    count={horseAccess.totalPages}
                    variant="outlined"
                    page={page}
                    onChange={(event, value) => { setPage(value); }}
                    disabled={isFetchingBarnHorse || isFetchingHorseAccess}
                  />
                )}

              </TableContainer>
            </CardContent>
          )
      }
      {
        !!snackbar && (
          <Snackbar
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            open
            onClose={() => { setSnackbar(null); }}
            autoHideDuration={10000}
          >
            <Alert {...snackbar} onClose={() => { setSnackbar(null); }} />
          </Snackbar>
        )
      }
      <AddEditModal
        open={openAddEditModal}
        horseAccess={selectedHorseAccess}
        horseId={id}
        onClose={handleCloseAddEditModal}
      />
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
        aria-describedby="info-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: 400,
            maxWidth: 800,
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              background: mode === 'light'
                ? primary.main
                : 'var(--template-palette-background-paper)',
              color: '#fff',
              p: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h5" component="h2" id="info-modal-title">
              About User Access with Questri Moible
            </Typography>
            <IconButton onClick={() => setShowInfoModal(false)}><Close sx={{ color: '#fff' }} /></IconButton>
          </Box>
          <Box
            sx={{
              p: 3,
              maxHeight: '70vh',
              overflowY: 'auto',
            }}
          >
            <Typography id="info-modal-description" paddingBottom>
              When you give a user access to a horse, that user will be available for
              you to use as a particpant or assinged user or rider on tasks related to the horse.
              This is usefull for organization on the barn board where
              rides, lessons, and other activities can be grouped by client.
            </Typography>
            <Typography paddingBottom>
              Giving a user horse access <b>DOES NOT</b> give that user privilages to
              view/interact with the horse in Questri (web or mobile). Advanced features
              for both the client and Questri Barn are only enabled when you
              enable Horse Access on the horse.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h5" component="h2" paddingBottom>
              Questri Horse User Access <small>(Professional Plan)</small>
            </Typography>
            <Typography paddingBottom>
              When you enable a paid client experience seat for a horse, you unlock
              many feature for both you and your client to make their experience with
              questri and your Questri Barn much more productive and enjoyable.
            </Typography>
            <Typography paddingBottom>
              Note that only users with the <b>Client</b> type gain access to
              these features, all others access types get limited access to the horse depending on their role.
            </Typography>
            <Typography variant="h6" component="h3" paddingBottom sx={{ mt: 2 }}>
              Admin Features
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemIcon sx={{ mr: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Share riding and lesson schedules with clients, take schedule change requests" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ mr: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Develop and share lesson plans, skills, rankings, etc." />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ mr: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Share horse notes with clients (photos, videos, show results, etc.)" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ mr: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Questri Announcements & Group Chats for announcements and chat boards" />
              </ListItem>
            </List>
            <Typography variant="h6" component="h3" paddingBottom sx={{ mt: 2 }}>
              Client Features
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem>
                <ListItemIcon sx={{ mr: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Access to Questri Mobile" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ mr: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="View horse info and select notes (lessons, pictures, videos)" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ mr: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="View schedule, request changes, track shows/results" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ mr: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Access announcements & group chats; manage assigned tasks" />
              </ListItem>
            </List>
          </Box>
        </Box>
      </Modal>
    </Paper>
  );
};

HorseAccessComponent.propTypes = {
  id: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool.isRequired,
};

HorseAccessComponent.defaultProps = {};

export default HorseAccessComponent;
